import axios from 'axios';
import { Endpoint } from 'types/url';
import { buildSearchParams } from 'utils/url';
import { BaseHttpClient } from '../BaseHttpClient';
import { injectCommonInterceptors } from '../interceptors';
import {
  GitLabGroup,
  GitlabConfigureMultiRequest,
  GitlabConfigureRequest,
  GitlabGetGroupsParams,
  GitlabGetRepositoriesParams,
  GitlabGroupWebhookInstallRequest,
  GitlabLoginMTRequest,
  GitlabLoginResponse,
  GitlabProvisionGroupTokenRequest,
  GitlabProvisionGroupsResponse,
  GitlabProvisionTokenRequest,
  GitlabProvisionTokenResponse,
  GitlabProvisioningConnectRequest,
  GitlabProvisioningConnectResponse,
  GitlabProvisioningGroupsAndProjectsPage,
  GitlabProvisioningGroupsAndProjectsRequest,
  GitlabProvisioningInstallationGroupsAndProjectsRequest,
  GitlabProvisioningManageRequest,
  GitlabProvisioningManageResponse,
  GitlabProvisioningManageStatusResponse,
  GitlabProvisioningProvisionRequest,
  GitlabProvisioningProvisionResponse,
  GitlabProvisioningStatusResponse,
  GitlabProvisioningUninstallRequest,
  GitlabProvisioningUninstallResponse,
  GitlabProvisioningUninstallStatusResponse,
  GitlabRepositoriesPage,
  GitlabWebhookInstallDispatched,
  GitlabWebhookInstallRequest,
  GitlabWebhookInstallResult,
} from './api.schema';
import { GIT_INTEGRATION_GITLAB_BASE_URL } from './consts';

const axiosGitlabInstance = axios.create({
  baseURL: GIT_INTEGRATION_GITLAB_BASE_URL,
});

injectCommonInterceptors(axiosGitlabInstance);

export class GitlabClient extends BaseHttpClient {
  constructor() {
    super(axiosGitlabInstance);
  }

  // Singleton instance for access without instantiation
  static override instance = new GitlabClient();

  static async loginMT(
    request: GitlabLoginMTRequest,
  ): Promise<GitlabLoginResponse> {
    return await GitlabClient.post('login-mt' as Endpoint, request);
  }

  static async login(): Promise<GitlabLoginResponse> {
    return await GitlabClient.post('login' as Endpoint, {});
  }

  static async configure(request: GitlabConfigureRequest): Promise<void> {
    await GitlabClient.post('configure' as Endpoint, request);
  }

  static async configureMulti(
    request: GitlabConfigureMultiRequest,
  ): Promise<void> {
    await GitlabClient.post('configure-multi' as Endpoint, request);
  }

  static async provisionGroups(): Promise<GitlabProvisionGroupsResponse> {
    return await GitlabClient.post('provision-groups' as Endpoint, {});
  }

  static async provisionGroupToken(
    request: GitlabProvisionGroupTokenRequest,
  ): Promise<GitlabProvisionGroupsResponse> {
    return await GitlabClient.post(
      'provision-group-token' as Endpoint,
      request,
    );
  }

  static async provisionToken(
    request: GitlabProvisionTokenRequest,
  ): Promise<GitlabProvisionTokenResponse> {
    return await GitlabClient.post('provision-token' as Endpoint, request);
  }

  static async loginRetry(): Promise<GitlabLoginResponse> {
    return await GitlabClient.post('login/retry' as Endpoint, {});
  }

  static async getGroups(
    params: GitlabGetGroupsParams,
  ): Promise<GitLabGroup[]> {
    const searchParams = buildSearchParams({
      app_config_id: params.appConfigId,
    });

    return await GitlabClient.get('groups' as Endpoint, {
      params: searchParams,
    });
  }

  static async getRepositories(
    params: GitlabGetRepositoriesParams,
  ): Promise<GitlabRepositoriesPage> {
    const searchParams = buildSearchParams({
      app_config_id: params.appConfigId,
      group_id: params.groupId,
      page: params.page,
      per_page: params.perPage,
      search: params.search,
    });

    return await GitlabClient.get('repositories' as Endpoint, {
      params: searchParams,
    });
  }

  static async installWebhooks(
    request: GitlabWebhookInstallRequest,
  ): Promise<GitlabWebhookInstallResult> {
    return await GitlabClient.post('webhooks' as Endpoint, request);
  }

  static async installWebhooksByGroups(
    request: GitlabGroupWebhookInstallRequest,
  ): Promise<GitlabWebhookInstallResult> {
    return await GitlabClient.post(
      'webhooks/install-by-groups' as Endpoint,
      request,
    );
  }

  static async installAllWebhooks(
    request: GitlabGroupWebhookInstallRequest,
  ): Promise<GitlabWebhookInstallDispatched> {
    return await GitlabClient.post('webhooks/install-all' as Endpoint, request);
  }

  // --- v2 wizard: provisioning ---

  static async provisioningConnect(
    request: GitlabProvisioningConnectRequest,
  ): Promise<GitlabProvisioningConnectResponse> {
    return await GitlabClient.post('provisioning/connect' as Endpoint, request);
  }

  static async provision(
    request: GitlabProvisioningProvisionRequest,
  ): Promise<GitlabProvisioningProvisionResponse> {
    return await GitlabClient.post(
      'provisioning/provision' as Endpoint,
      request,
    );
  }

  static async getProvisioningStatus(
    provisionId: string,
  ): Promise<GitlabProvisioningStatusResponse> {
    return await GitlabClient.get(
      `provisioning/provision/${provisionId}/status` as Endpoint,
    );
  }

  static async getProvisioningGroupsAndProjects(
    request: GitlabProvisioningGroupsAndProjectsRequest,
  ): Promise<GitlabProvisioningGroupsAndProjectsPage> {
    // POST (not GET): the PAT rides in the body, never a query string / log.
    return await GitlabClient.post(
      'provisioning/groups-and-projects' as Endpoint,
      request,
    );
  }

  static async getProvisioningInstallationGroupsAndProjects(
    installationId: string,
    request: GitlabProvisioningInstallationGroupsAndProjectsRequest,
  ): Promise<GitlabProvisioningGroupsAndProjectsPage> {
    // Manage variant: the server resolves scope from the install id (path), so no
    // gitlabUrl. Still POST — the elevation PAT rides in the body.
    return await GitlabClient.post(
      `provisioning/installations/${installationId}/groups-and-projects` as Endpoint,
      request,
    );
  }

  static async provisioningManage(
    request: GitlabProvisioningManageRequest,
  ): Promise<GitlabProvisioningManageResponse> {
    return await GitlabClient.post('provisioning/manage' as Endpoint, request);
  }

  static async getProvisioningManageStatus(
    manageId: string,
  ): Promise<GitlabProvisioningManageStatusResponse> {
    return await GitlabClient.get(
      `provisioning/manage/${manageId}/status` as Endpoint,
    );
  }

  static async provisioningUninstall(
    request: GitlabProvisioningUninstallRequest,
  ): Promise<GitlabProvisioningUninstallResponse> {
    return await GitlabClient.post(
      'provisioning/uninstall' as Endpoint,
      request,
    );
  }

  static async getProvisioningUninstallStatus(
    uninstallId: string,
  ): Promise<GitlabProvisioningUninstallStatusResponse> {
    return await GitlabClient.get(
      `provisioning/uninstall/${uninstallId}/status` as Endpoint,
    );
  }
}
